import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Loader2, Save, Percent } from 'lucide-react';

/** ReferralTab — Level-1/2 commission rates + MRO onboarding bonus. */
export function ReferralTab({
  referral, setReferral, saveReferral,
  mroRate, setMroRate, saveMroRate,
  saving,
}) {
  return (
    <Card className="bg-[#041A3B] border-white/10">
      <CardHeader>
        <CardTitle className="text-white">Referral Program Settings</CardTitle>
        <CardDescription className="text-[#A0B4CC]">Configure commission rates and rules</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between p-4 rounded-lg bg-[#000B18]">
          <div>
            <h4 className="font-medium text-white">Enable Referral Program</h4>
            <p className="text-[#5C7599] text-sm">Allow members to earn commissions</p>
          </div>
          <Switch
            checked={referral.is_enabled}
            onCheckedChange={(checked) => setReferral({ ...referral, is_enabled: checked })}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-white">Level 1 — Direct Referral (%)</Label>
            <div className="relative">
              <Input
                type="number" min="0" max="100" step="0.5"
                value={referral.level_1_rate * 100}
                onChange={(e) => setReferral({ ...referral, level_1_rate: parseFloat(e.target.value) / 100 })}
                className="bg-[#000B18] border-white/20 text-white pr-8"
                data-testid="level-1-rate-input"
              />
              <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
            </div>
            <p className="text-[#5C7599] text-xs">Paid to direct referrer (recommended: 10%)</p>
          </div>
          <div className="space-y-2">
            <Label className="text-white">Level 2 — Indirect Referral (%)</Label>
            <div className="relative">
              <Input
                type="number" min="0" max="100" step="0.5"
                value={referral.level_2_rate * 100}
                onChange={(e) => setReferral({ ...referral, level_2_rate: parseFloat(e.target.value) / 100 })}
                className="bg-[#000B18] border-white/20 text-white pr-8"
                data-testid="level-2-rate-input"
              />
              <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
            </div>
            <p className="text-[#5C7599] text-xs">Paid to referrer&apos;s referrer (recommended: 5%)</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-white">Minimum Withdrawal (PHP)</Label>
            <Input
              type="number" min="0"
              value={referral.min_withdrawal}
              onChange={(e) => setReferral({ ...referral, min_withdrawal: parseFloat(e.target.value) })}
              className="bg-[#000B18] border-white/20 text-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-white">Maximum Levels</Label>
            <select
              value={referral.max_levels}
              onChange={(e) => setReferral({ ...referral, max_levels: parseInt(e.target.value) })}
              className="w-full p-2 rounded-md bg-[#000B18] border border-white/20 text-white"
            >
              <option value={1}>1 Level</option>
              <option value={2}>2 Levels</option>
              <option value={3}>3 Levels</option>
            </select>
          </div>
        </div>

        <Button onClick={saveReferral} disabled={saving} className="btn-primary" data-testid="save-referral-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Referral Settings
        </Button>

        <div className="pt-6 border-t border-white/10">
          <h4 className="font-medium text-white mb-1">MRO Onboarding Bonus</h4>
          <p className="text-[#5C7599] text-sm mb-3">Flat amount (PHP) paid to the MRO for each merchant they successfully onboard. Paid once per approved merchant.</p>
          <div className="flex items-end gap-3">
            <div className="flex-1 space-y-2">
              <Label className="text-white text-sm">Bonus per Merchant (PHP)</Label>
              <div className="relative">
                <Input
                  type="number" min="0" max="100000" step="0.01"
                  value={mroRate}
                  onChange={(e) => setMroRate(e.target.value)}
                  className="bg-[#000B18] border-white/20 text-white pl-8"
                  data-testid="mro-rate-input"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C7599] text-sm">₱</span>
              </div>
            </div>
            <Button onClick={saveMroRate} disabled={saving} className="btn-primary" data-testid="save-mro-rate-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Bonus
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default ReferralTab;
